import Autosuggest, {
  AutosuggestListOption,
} from "@/core/components/Autosuggest/Autosuggest";
import { FieldProps } from "@/core/components/Field";
import { filterAirOptions, isAirData, stringifyAirData } from "./common";
import { getAirlines } from "@/api/airlinesApi";
import { useCallback, useEffect, useRef } from "react";

export const AirlinesAutosuggest = ({ ...props }: FieldProps) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create a new controller and abort previous request if exists
  const createNewAbortController = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  }, []);

  const getData = useCallback(async (input: string) => {
    const controller = createNewAbortController();

    try {
      const { airlines } = await getAirlines(input, controller.signal);
      return airlines;
    } catch (error) {
      return [];
    }
  }, []);

  return (
    <Autosuggest
      {...props}
      handleDefaultValue={({
        currentDefaultValue,
        setInputValue,
        setAutosuggestInput,
        setValue,
      }) => {
        if (
          typeof currentDefaultValue === "object" &&
          isAirData(currentDefaultValue)
        ) {
          setInputValue(stringifyAirData(currentDefaultValue));
          setAutosuggestInput(currentDefaultValue.iata);
          setValue(props.field.key, currentDefaultValue);
        }
      }}
      getData={getData}
      getInputFromOption={(option) => {
        if (isAirData(option)) {
          return option.iata;
        }
      }}
      filterOptions={filterAirOptions}
      getLabelForOption={(option) => {
        if (isAirData(option)) {
          return stringifyAirData(option);
        }
        return option.toString();
      }}
      isOptionEqualToValue={(
        option: AutosuggestListOption,
        autosuggestInput: string
      ) => {
        if ("iata" in option) {
          return option.iata === autosuggestInput;
        }
        return false;
      }}
    />
  );
};
